import { FormEvent, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Plus,
  Settings2,
  BarChart3,
  Link2,
  UserPlus,
  Printer,
  Copy,
  Code,
  MoreHorizontal,
  Loader2,
  Search,
  CalendarDays,
  MapPin,
  ExternalLink,
  LayoutGrid,
  List,
  Clock,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventItem {
  id: number
  name: string
  event_date: string | null
  active: boolean
  link_token: string
  setup_url: string
  dashboard_url: string
  registration_url: string
  self_registration_url: string
  event_users_url: string
  clone_url: string
  frame_registration_url: string
}

interface EventsIndexProps {
  events: EventItem[]
  canCreate: boolean
  canEdit: boolean
  canDestroy: boolean
  showBadges: boolean
  createEventUrl: string
  locale: string
  logoUrl: string | null
  accountName: string
  userEmail: string
  canEditAccount: boolean
  urls: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    whatsappTemplates?: string
    invitations?: string
  }
  translations: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    logoutConfirm: string
    logoutConfirmBody: string
    cancel: string
    whatsappTemplates?: string
    invitations?: string
    newEvent: string
    edit: string
    dashboard: string
    registrationLink: string
    selfRegistrationLink: string
    printBadges: string
    clone: string
    embed: string
    options: string
    search: string
    noEvents: string
    noEventsDesc: string
    eventName: string
    eventDate: string
    aboutEvent: string
    eventLocation: string
    hasAgenda: string
    hasSpeakers: string
    hasPartners: string
    hasExternalLinks: string
    hasSocialLinks: string
    yes: string
    no: string
    create: string
    copyEmbedCode: string
    copied: string
  }
}

/* ---- Notion-style emoji colours keyed on event id ---- */
const EVENT_ICONS = ['📅', '🎤', '🎯', '🚀', '✨', '🎪', '📢', '🏆', '💡', '🌍']
const ICON_BG = [
  'bg-[#FBE4E4]', 'bg-[#FADEC9]', 'bg-[#FDECC8]', 'bg-[#DBEDDB]',
  'bg-[#D3E5EF]', 'bg-[#E8DEEE]', 'bg-[#F5E0E9]', 'bg-[#FADEC9]',
  'bg-[#FDECC8]', 'bg-[#DBEDDB]',
]

function getEventIcon(id: number) {
  return EVENT_ICONS[id % EVENT_ICONS.length]
}
function getEventBg(id: number) {
  return ICON_BG[id % ICON_BG.length]
}

function formatDate(dateStr: string | null, locale: string) {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EventsIndex({
  events,
  canCreate,
  canEdit,
  canDestroy,
  showBadges,
  createEventUrl,
  locale,
  logoUrl,
  accountName,
  userEmail,
  canEditAccount,
  urls,
  translations: t,
}: EventsIndexProps) {
  const [createOpen, setCreateOpen] = useState(false)
  const [embedOpen, setEmbedOpen] = useState(false)
  const [embedCode, setEmbedCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [processing, setProcessing] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchVisible, setSearchVisible] = useState(false)

  const [form, setForm] = useState({
    name: '',
    event_date: '',
    about: '',
    location: '',
    has_agenda: true,
    has_speakers: true,
    has_partners: true,
    has_external_links: true,
    has_social_links: true,
  })

  const handleCreate = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(createEventUrl, { event: form }, {
      onFinish: () => setProcessing(false),
      onSuccess: () => {
        setCreateOpen(false)
        setForm({ name: '', event_date: '', about: '', location: '', has_agenda: true, has_speakers: true, has_partners: true, has_external_links: true, has_social_links: true })
      },
    })
  }

  const handleEmbed = (url: string) => {
    setEmbedCode(`<iframe src="${url}" frameborder="0" width="100%" height="600"></iframe>`)
    setEmbedOpen(true)
    setCopied(false)
  }

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const filtered = events.filter((ev) =>
    ev.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <>
      <Head title={t.events} />

      <AppShell
        locale={locale}
        logoUrl={logoUrl}
        accountName={accountName}
        userEmail={userEmail}
        canEditAccount={canEditAccount}
        urls={urls}
        translations={{
          events: t.events,
          accountSettings: t.accountSettings,
          changePassword: t.changePassword,
          logout: t.logout,
          logoutConfirm: t.logoutConfirm,
          logoutConfirmBody: t.logoutConfirmBody,
          cancel: t.cancel,
          whatsappTemplates: t.whatsappTemplates,
          invitations: t.invitations,
        }}
      >
        {/* ─── Page icon + title (Notion-style) ─── */}
        <div className="pt-8 pb-1">
          <div className="text-4xl mb-1.5">📅</div>
          <h1 className="text-3xl font-bold text-[#37352F] tracking-tight leading-tight">
            {t.events}
          </h1>
          <p className="text-sm text-[#91918E] mt-1">
            {filtered.length} {filtered.length === 1 ? 'event' : 'events'}
          </p>
        </div>

        {/* ─── Inline toolbar ─── */}
        <div className="flex items-center gap-1 py-3 border-b border-[#E8E8E5] mb-0.5">
          {/* Search toggle */}
          <button
            onClick={() => setSearchVisible(!searchVisible)}
            className={`flex items-center gap-1 rounded-[4px] px-2 py-1 text-xs transition-colors cursor-pointer ${
              searchVisible ? 'bg-[#F1F1EF] text-[#37352F]' : 'text-[#91918E] hover:bg-[#F1F1EF]'
            }`}
          >
            <Search className="h-3.5 w-3.5" />
            {t.search}
          </button>

          {/* View mode toggles */}
          <div className="flex items-center border border-[#E8E8E5] rounded-[4px] overflow-hidden ltr:ml-1 rtl:mr-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center justify-center h-6 w-7 transition-colors cursor-pointer ${
                viewMode === 'grid' ? 'bg-[#F1F1EF] text-[#37352F]' : 'text-[#C4C4C0] hover:text-[#91918E] hover:bg-[#F7F7F5]'
              }`}
            >
              <LayoutGrid className="h-3 w-3" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center justify-center h-6 w-7 transition-colors cursor-pointer ${
                viewMode === 'list' ? 'bg-[#F1F1EF] text-[#37352F]' : 'text-[#C4C4C0] hover:text-[#91918E] hover:bg-[#F7F7F5]'
              }`}
            >
              <List className="h-3 w-3" />
            </button>
          </div>

          <div className="flex-1" />

          {/* New button */}
          {canCreate && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger className="flex items-center gap-1 rounded-[4px] px-2 py-1 text-xs font-medium text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer">
                <Plus className="h-3.5 w-3.5" />
                {t.newEvent}
              </DialogTrigger>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle className="text-[#37352F] text-lg">{t.newEvent}</DialogTitle>
                  <DialogDescription className="text-[#91918E]" />
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.eventName}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      placeholder="My Event"
                      className="mt-1 h-9 rounded-[4px] border-[#E8E8E5] text-[#37352F] placeholder:text-[#C4C4C0] focus-visible:ring-[#2383E2]/30 focus-visible:border-[#2383E2]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.eventDate}</Label>
                    <Input
                      type="date"
                      value={form.event_date}
                      onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                      required
                      className="mt-1 h-9 rounded-[4px] border-[#E8E8E5] text-[#37352F]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.aboutEvent}</Label>
                    <Input
                      value={form.about}
                      onChange={(e) => setForm({ ...form, about: e.target.value })}
                      required
                      placeholder="A short description..."
                      className="mt-1 h-9 rounded-[4px] border-[#E8E8E5] text-[#37352F] placeholder:text-[#C4C4C0]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-[#91918E] uppercase tracking-wider">{t.eventLocation}</Label>
                    <Input
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      required
                      placeholder="Riyadh, SA"
                      className="mt-1 h-9 rounded-[4px] border-[#E8E8E5] text-[#37352F] placeholder:text-[#C4C4C0]"
                    />
                  </div>

                  {showBadges && (
                    <div className="rounded-[4px] border border-[#E8E8E5] p-3 space-y-2.5 bg-[#FBFBFA]">
                      <p className="text-xs font-medium text-[#91918E] uppercase tracking-wider">Features</p>
                      {([
                        ['has_agenda', t.hasAgenda],
                        ['has_speakers', t.hasSpeakers],
                        ['has_partners', t.hasPartners],
                        ['has_external_links', t.hasExternalLinks],
                        ['has_social_links', t.hasSocialLinks],
                      ] as const).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between">
                          <Label className="text-sm text-[#37352F]">{label}</Label>
                          <Switch
                            checked={form[key as keyof typeof form] as boolean}
                            onCheckedChange={(v) => setForm({ ...form, [key]: v })}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <DialogFooter className="gap-2 sm:gap-0 pt-1">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="border-[#E8E8E5] text-[#37352F] hover:bg-[#F1F1EF]">
                      {t.cancel}
                    </Button>
                    <Button type="submit" disabled={processing} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white">
                      {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : t.create}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search bar (collapsible) */}
        {searchVisible && (
          <div className="border-b border-[#E8E8E5] py-2 animate-in slide-in-from-top-1 duration-150">
            <div className="relative max-w-sm">
              <Search className="absolute ltr:left-2 rtl:right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#C4C4C0] pointer-events-none" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`${t.search}...`}
                className="w-full h-8 ltr:pl-7 rtl:pr-7 pr-3 rounded-[4px] border border-[#E8E8E5] bg-white text-sm text-[#37352F] placeholder:text-[#C4C4C0] focus:outline-none focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]/30 transition-colors"
              />
            </div>
          </div>
        )}

        {/* ─── Content ─── */}
        {filtered.length === 0 ? (
          /* Empty state */
          <div className="py-20 text-center">
            <div className="text-5xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-[#37352F] mb-1">{t.noEvents}</h3>
            <p className="text-sm text-[#91918E]">{t.noEventsDesc}</p>
            {canCreate && (
              <button
                onClick={() => setCreateOpen(true)}
                className="mt-4 inline-flex items-center gap-1 text-sm text-[#2383E2] hover:underline cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                {t.newEvent}
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* ── Grid view (Notion board style) ── */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-3">
            {filtered.map((event) => (
              <a
                key={event.id}
                href={event.dashboard_url}
                className="group rounded-lg border border-[#E8E8E5] bg-white px-3 py-3 hover:bg-[#FBFBFA] hover:shadow-sm transition-all block"
              >
                {/* Icon + name */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`flex h-7 w-7 items-center justify-center rounded text-sm shrink-0 ${getEventBg(event.id)}`}>
                    {getEventIcon(event.id)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-[#37352F] truncate leading-tight">{event.name}</h3>
                  </div>
                  {canDestroy && (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        onClick={(e) => e.preventDefault()}
                        className="shrink-0 flex items-center justify-center h-5 w-5 rounded opacity-0 group-hover:opacity-100 hover:bg-[#EDEDEC] text-[#91918E] cursor-pointer outline-none transition-opacity"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={4}>
                        <DropdownMenuItem render={<a href={event.clone_url} />} className="cursor-pointer gap-2 text-[#37352F]">
                          <Copy className="h-4 w-4 text-[#91918E]" />
                          {t.clone}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => { e.preventDefault(); handleEmbed(event.frame_registration_url) }}
                          className="cursor-pointer gap-2 text-[#37352F]"
                        >
                          <Code className="h-4 w-4 text-[#91918E]" />
                          {t.embed}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Meta */}
                <div className="flex items-center gap-2 text-[11px] text-[#91918E]">
                  {event.event_date && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(event.event_date, locale)}
                    </span>
                  )}
                  {event.active && (
                    <span className="inline-flex items-center rounded-full bg-[#DBEDDB] px-1.5 py-0.5 text-[10px] font-medium text-[#4B7A4B]">
                      Active
                    </span>
                  )}
                </div>

                {/* Quick links */}
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#F1F1EF]">
                  {canEdit && (
                    <span
                      onClick={(e) => { e.preventDefault(); window.location.href = event.setup_url }}
                      className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] text-[#91918E] hover:bg-[#F1F1EF] hover:text-[#37352F] transition-colors cursor-pointer"
                    >
                      <Settings2 className="h-3 w-3" />
                      {t.edit}
                    </span>
                  )}
                  <span
                    onClick={(e) => { e.preventDefault(); window.open(event.registration_url, '_blank') }}
                    className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] text-[#2383E2] hover:bg-[#EBF5FF] transition-colors cursor-pointer"
                  >
                    <Link2 className="h-3 w-3" />
                    Link
                  </span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          /* ── List view (Notion table style) ── */
          <div className="mt-px">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_120px_100px_100px] sm:grid-cols-[1fr_140px_120px_120px_auto] gap-0 border-b border-[#E8E8E5] text-xs text-[#91918E] font-medium uppercase tracking-wider">
              <div className="px-2 py-1.5">{t.eventName}</div>
              <div className="px-2 py-1.5">{t.eventDate}</div>
              <div className="px-2 py-1.5 hidden sm:block">{t.dashboard}</div>
              <div className="px-2 py-1.5">{t.registrationLink}</div>
              <div className="px-2 py-1.5 w-8" />
            </div>

            {/* Rows */}
            {filtered.map((event) => (
              <div
                key={event.id}
                className="group grid grid-cols-[1fr_120px_100px_100px] sm:grid-cols-[1fr_140px_120px_120px_auto] gap-0 border-b border-[#F1F1EF] hover:bg-[#FBFBFA] transition-colors items-center"
              >
                {/* Name cell */}
                <div className="px-2 py-2 flex items-center gap-2 min-w-0">
                  <span className={`flex h-6 w-6 items-center justify-center rounded text-xs shrink-0 ${getEventBg(event.id)}`}>
                    {getEventIcon(event.id)}
                  </span>
                  <a
                    href={canEdit ? event.setup_url : event.dashboard_url}
                    className="text-sm text-[#37352F] hover:underline font-medium truncate"
                  >
                    {event.name}
                  </a>
                  {event.active && (
                    <span className="shrink-0 inline-flex items-center rounded-full bg-[#DBEDDB] px-1.5 py-0.5 text-[10px] font-medium text-[#4B7A4B]">
                      Active
                    </span>
                  )}
                </div>

                {/* Date cell */}
                <div className="px-2 py-2 text-xs text-[#91918E]">
                  {formatDate(event.event_date, locale) || '—'}
                </div>

                {/* Dashboard */}
                <div className="px-2 py-2 hidden sm:block">
                  <a
                    href={event.dashboard_url}
                    className="inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-xs text-[#91918E] hover:bg-[#F1F1EF] hover:text-[#37352F] transition-colors"
                  >
                    <BarChart3 className="h-3 w-3" />
                    {t.dashboard}
                  </a>
                </div>

                {/* Registration */}
                <div className="px-2 py-2">
                  <a
                    href={event.registration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-[#2383E2] hover:underline"
                  >
                    <Link2 className="h-3 w-3" />
                    Link
                    <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                  </a>
                </div>

                {/* More button */}
                <div className="px-1 py-2 w-8">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center justify-center h-6 w-6 rounded-[4px] opacity-0 group-hover:opacity-100 hover:bg-[#EDEDEC] text-[#91918E] cursor-pointer outline-none transition-opacity">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={4}>
                      {canEdit && (
                        <DropdownMenuItem render={<a href={event.setup_url} />} className="cursor-pointer gap-2 text-[#37352F]">
                          <Settings2 className="h-4 w-4 text-[#91918E]" />
                          {t.edit}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem render={<a href={event.dashboard_url} />} className="cursor-pointer gap-2 text-[#37352F]">
                        <BarChart3 className="h-4 w-4 text-[#91918E]" />
                        {t.dashboard}
                      </DropdownMenuItem>
                      <DropdownMenuItem render={<a href={event.self_registration_url} target="_blank" rel="noopener noreferrer" />} className="cursor-pointer gap-2 text-[#37352F]">
                        <UserPlus className="h-4 w-4 text-[#91918E]" />
                        {t.selfRegistrationLink}
                      </DropdownMenuItem>
                      {showBadges && (
                        <DropdownMenuItem render={<a href={event.event_users_url} />} className="cursor-pointer gap-2 text-[#37352F]">
                          <Printer className="h-4 w-4 text-[#91918E]" />
                          {t.printBadges}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-[#E8E8E5]" />
                      <DropdownMenuItem render={<a href={event.clone_url} />} className="cursor-pointer gap-2 text-[#37352F]">
                        <Copy className="h-4 w-4 text-[#91918E]" />
                        {t.clone}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEmbed(event.frame_registration_url)} className="cursor-pointer gap-2 text-[#37352F]">
                        <Code className="h-4 w-4 text-[#91918E]" />
                        {t.embed}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}

            {/* New row hint */}
            {canCreate && (
              <button
                onClick={() => setCreateOpen(true)}
                className="flex w-full items-center gap-2 px-2 py-2 text-xs text-[#C4C4C0] hover:text-[#91918E] hover:bg-[#FBFBFA] transition-colors border-b border-[#F1F1EF] cursor-pointer"
              >
                <Plus className="h-3 w-3" />
                {t.newEvent}
              </button>
            )}
          </div>
        )}

        {/* ─── Embed code dialog ─── */}
        <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#37352F]">
                <Code className="h-4 w-4 text-[#91918E]" />
                {t.embed}
              </DialogTitle>
              <DialogDescription className="text-[#91918E]">{t.copyEmbedCode}</DialogDescription>
            </DialogHeader>
            <textarea
              readOnly
              value={embedCode}
              rows={4}
              dir="ltr"
              className="w-full rounded-[4px] border border-[#E8E8E5] bg-[#FBFBFA] p-3 text-sm font-mono text-[#37352F] focus:outline-none focus:ring-1 focus:ring-[#2383E2]/30 focus:border-[#2383E2]"
            />
            <DialogFooter>
              <Button onClick={handleCopyEmbed} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white">
                {copied ? (
                  <span className="flex items-center gap-1.5"><Copy className="h-4 w-4" />{t.copied}</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Copy className="h-4 w-4" />{t.copyEmbedCode}</span>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}
